// @ts-check
import { useMasquerade } from 'modules/oidc/useAuthenticated';
import { useEffect, useMemo, useRef } from 'react';
import tourConfigs from './config';
import useIntroContext from './context';
import { useIntroCompleted, useIntroPaused } from './useIntroStates';

const INTRO_PAUSED_MS = 72e6; // 20 hours

/**
 * @param {object} props
 * @param {string} props.id
 * @param {import('./config').IntroJsOptions} [props.introJsOptions]
 * @param {boolean} [props.shouldPause]
 */
export function IntroTourLoader(props) {
	const isMasquerade = useMasquerade();
	const { id, introJsOptions, shouldPause } = props;
	const { startTour, introTargets } = useIntroContext();
	const startedRef = useRef(false);

	const [paused, setPaused, { isBusy: isPausedBusy, isError: isPausedError }] =
		useIntroPaused(id);

	const [
		isComplete,
		setIsComplete,
		{ isBusy: isCompleteBusy, isError: isCompleteError },
	] = useIntroCompleted(id);

	const isPaused = isPausedBusy || Date.now() < (paused || 0) || isPausedError;
	const isNotComplete = !isCompleteBusy && !isComplete && !isCompleteError;
	const allowTour = isNotComplete && !isPaused && !isMasquerade;

	// Store the tour steps
	const tourSteps = useMemo(() => {
		if (!allowTour) return undefined;

		const { steps: stepsConfig } = tourConfigs[id];

		const steps = [];
		for (const step of stepsConfig) {
			const { elementTarget, allowInvisible, ...stepOption } = step;

			if (elementTarget) {
				const target = introTargets[elementTarget];

				// Bail if the target doesnt exist yet
				if (!target) return undefined;
				const { ref, isVisible } = target;

				// Bail if something happened to the ref
				if (!ref.current) return undefined;

				// Bail if the target isnt visible
				if (!isVisible && !allowInvisible) return undefined;

				stepOption.element = ref.current;
			}

			// Bail if the element should exist but doesnt
			if ('element' in stepOption && !stepOption.element) return undefined;

			steps.push(stepOption);
		}
		return steps;
	}, [id, allowTour, introTargets]);

	// Start tour _when able to_
	useEffect(() => {
		if (!tourSteps) return;

		// Dont run it twice per load
		if (startedRef.current) return;
		startedRef.current = true;

		startTour(tourSteps, {
			onStarted: () => {
				if (shouldPause) {
					// Don't show for a while even if they didnt finish
					setPaused(Date.now() + INTRO_PAUSED_MS);
				}
			},
			onExit: () => {
				if (shouldPause) return;
				setIsComplete(true);
			},
			onComplete: () => {
				setIsComplete(true);
				if (shouldPause) {
					setPaused(undefined);
				}
			},
			...introJsOptions,
		});
	}, [
		tourSteps,
		setPaused,
		setIsComplete,
		startTour,
		introJsOptions,
		shouldPause,
	]);

	return null;
}

export default IntroTourLoader;
